import { Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Layout } from '@roc/client-portal-shared/components';
import { LoanSubType, TRUE } from '@roc/feature-utils';
import { Paper } from '@roc/ui';
import { LoanSubmissionStep } from 'libs/feature-loans/src/loanSubmission/utils/constants';
import { PROPERTY_INFORMATION_STEP, LOAN_ECONOMICS_STEP, QUOTE_SUMMARY_STEP, QUOTE_COMPLETE_STEP } from '../utils/constants';
import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { useBorrowerLoanSubmissionStore } from '../hooks/useBorrowerLoanSubmissionStore';
import { SinglePropertyPropertyInformationStep } from './borrowerLoanSubmissionSteps/propertyInformationStep'
import { SinglePropertyLoanEconomicsStep } from './borrowerLoanSubmissionSteps/loanEconomicsStep'
import { QuoteSummaryStep } from './borrowerLoanSubmissionSteps/quoteSummaryStep'
import { QuoteCompleteStep } from './borrowerLoanSubmissionSteps/quoteCompleteStep'
import { ProgressBarStepper } from './stepper/progressBarStepper';
import { SuccessPage } from './successPage';
import { is } from 'date-fns/locale';
import { BorrowerEntityInformationStepV2 } from './borrowerEntityV2/borrowerEntityInformationStepV2';
import { LoanTypeStoreContext } from '../utils/loanTypeStoreContext';
import { useHistory } from 'react-router-dom';

const useStyles = makeStyles(theme => ({
  paper: {
    borderRadius: theme.spacing(2),
    boxShadow:
      'rgb(145 158 171 / 24%) 0px 0px 2px 0px, rgb(145 158 171 / 24%) 0px 16px 32px -4px',
  },
  title: {
    fontSize: '48px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '116.7%',
    padding: theme.spacing(2),
  },
}));

export const BorrowerSinglePropertyRentalLoanSubmission = observer(
  ({ currentRoutes }) => {
    const classes = useStyles();
    const history = useHistory();
    const { borrowerLoanSubmissionStore } = useBorrowerLoanSubmissionStore();
    const { singlePropertyQuoteStore } = borrowerLoanSubmissionStore;
    const baseUrl = borrowerLoanSubmissionStore?.globalStore?.routeBaseUrl
    const handlePropertyInformationStep = (step: number) => {
      const isValid = singlePropertyQuoteStore?.singlePropertyPropertyStore?.validatePropertyByStep(step);
      const income = singlePropertyQuoteStore?.loanEconomicsStore?.calculateIncomeAndExpenses();
      if (isValid) {
        if (PROPERTY_INFORMATION_STEP.TELL_US_ABOUT_YOUR_RENTAL_HOME.subStep === step && income?.calculatedOperatingIncome < 0) {
          singlePropertyQuoteStore.globalStore.notificationStore.showWarningNotification({
            message:
              'Property must have positive income to qualify for a loan, please verify monthly income and annual expenses provided',
          });
        }
        else {
          handleNext()
        }
      }
    }
    const fetchBorrowerData = async () => {
      await singlePropertyQuoteStore?.borrowerEntityInformationStore.fetchBorrowerAndEntityData();
      await singlePropertyQuoteStore?.borrowerEntityInformationStore.goToFirstSubstep();
    }
    useEffect(() => {
      singlePropertyQuoteStore.resetQuoteForms();
    }, []);
    const handleLoanEconomicsStep = () => {
      const isValid = singlePropertyQuoteStore?.loanEconomicsStore.validateFields()
      if (isValid) {
        handleNext()
      }
    }
    const acceptQuote = () => {
      singlePropertyQuoteStore.submitLoan()
    }
    const handleComplete = () => {
      singlePropertyQuoteStore.resetQuoteForms();
      history.push(currentRoutes?.url)
    }
    const handleNext = () => {
      const currentStep = steps[singlePropertyQuoteStore.activeStep].type;
      singlePropertyQuoteStore.handleNext(
        steps[singlePropertyQuoteStore.activeStep].type
      );
      if(currentStep === LoanSubmissionStep.PROPERTY_INFORMATION_BORROWER_SUBMISSION && steps[singlePropertyQuoteStore.activeStep].type === LoanSubmissionStep.ENTITY_INFORMATION) {
        fetchBorrowerData();
      }
    };

    const handleBack = () => {
      const currentStep = steps[singlePropertyQuoteStore.activeStep].type;
      if ("BORROWER_CREDIT" === singlePropertyQuoteStore?.borrowerEntityInformationStore.borrowerEntityInformationActiveStep && singlePropertyQuoteStore?.borrowerInformationStore?.mainBorrower?.citizenShipStatus) {
        singlePropertyQuoteStore.handleBack(
          steps[singlePropertyQuoteStore.activeStep].type
        );
      }
      singlePropertyQuoteStore.handleBack(
        steps[singlePropertyQuoteStore.activeStep].type
      );
      if(currentStep === LoanSubmissionStep.ENTITY_INFORMATION && !steps[singlePropertyQuoteStore.activeStep].type) {
        handlePropertyInformationStep(PROPERTY_INFORMATION_STEP.TELL_US_ABOUT_YOUR_PROPERTY.subStep);
      }
    };
    useEffect(() => {
      singlePropertyQuoteStore.resetForNewLoanApplication();
      singlePropertyQuoteStore.initialize();
      singlePropertyQuoteStore.setLoanSubtype(LoanSubType.SINGLE_PROPERTY);
    }, []);
    // TOD0: Add steps here
    const steps = [
      {
        label: PROPERTY_INFORMATION_STEP.TELL_US_ABOUT_YOUR_PROPERTY.label,
        icon: null,
        contentHasBorder: true,
        content: <SinglePropertyPropertyInformationStep quoteStore={singlePropertyQuoteStore} substep={LOAN_ECONOMICS_STEP.CHOOSING_YOUR_LOAN_TERMS.subStep} store={singlePropertyQuoteStore?.singlePropertyPropertyStore?.propertyFormStore} />,
        onBack: () => handleBack(),
        onNext: () => handlePropertyInformationStep(PROPERTY_INFORMATION_STEP.TELL_US_ABOUT_YOUR_PROPERTY.subStep),
        allowBack: false,
        allowNext: true,
        buttonText: 'NEXT',
        labelIndexNumber: PROPERTY_INFORMATION_STEP.TELL_US_ABOUT_YOUR_PROPERTY.labelIndexNumber,
        subLabel: PROPERTY_INFORMATION_STEP.TELL_US_ABOUT_YOUR_PROPERTY.subLabel,
      },
      {
        label: PROPERTY_INFORMATION_STEP.TELL_US_ABOUT_YOUR_RENTAL_HOME.label,
        icon: null,
        contentHasBorder: true,
        type: LoanSubmissionStep.PROPERTY_INFORMATION_BORROWER_SUBMISSION,
        content: <SinglePropertyPropertyInformationStep quoteStore={singlePropertyQuoteStore} substep={PROPERTY_INFORMATION_STEP.TELL_US_ABOUT_YOUR_RENTAL_HOME.subStep} store={singlePropertyQuoteStore?.singlePropertyPropertyStore?.propertyFormStore} />,
        onBack: () => handleBack(),
        onNext: () => handlePropertyInformationStep(PROPERTY_INFORMATION_STEP.TELL_US_ABOUT_YOUR_RENTAL_HOME.subStep),
        allowBack: true,
        allowNext: true,
        buttonText: 'NEXT',
        labelIndexNumber: PROPERTY_INFORMATION_STEP.TELL_US_ABOUT_YOUR_RENTAL_HOME.labelIndexNumber,
        subLabel: PROPERTY_INFORMATION_STEP.TELL_US_ABOUT_YOUR_RENTAL_HOME.subLabel,
      },
      {
        label: 'Borrowing Entity Information',
        icon: null,
        type: LoanSubmissionStep.ENTITY_INFORMATION,
        content: <BorrowerEntityInformationStepV2 store={singlePropertyQuoteStore} />,
        onBack: () => handleBack(),
        onNext: () => handleNext(),
        allowBack: true,
        allowNext: singlePropertyQuoteStore.borrowerEntityInformationStore.allowNext,
        buttonText: 'NEXT',
        labelIndexNumber: 2,
        subLabels: '',
      },
      {
        label: LOAN_ECONOMICS_STEP.CHOOSING_YOUR_LOAN_TERMS.label,
        icon: null,
        contentHasBorder: true,
        type: LoanSubmissionStep.LOAN_ECONOMICS,
        content: <SinglePropertyLoanEconomicsStep onNext={handleLoanEconomicsStep} loanEconomicsStore={singlePropertyQuoteStore?.loanEconomicsStore
        } substep={LOAN_ECONOMICS_STEP.CHOOSING_YOUR_LOAN_TERMS.subStep} store={singlePropertyQuoteStore?.loanEconomicsStore
          ?.loanEconomicsFormStore} />,
        onBack: () => handleBack(),
        onNext: () => handleLoanEconomicsStep(),
        allowBack: true,
        allowNext: false,
        buttonText: 'NEXT',
        labelIndexNumber: LOAN_ECONOMICS_STEP.CHOOSING_YOUR_LOAN_TERMS.labelIndexNumber,
        subLabel: LOAN_ECONOMICS_STEP.CHOOSING_YOUR_LOAN_TERMS.subLabel,
      },
      {
        label: QUOTE_SUMMARY_STEP.QUOTE_SUMMARY_STEP.label,
        icon: null,
        contentHasBorder: true,
        content: <QuoteSummaryStep substep={LOAN_ECONOMICS_STEP.CHOOSING_YOUR_LOAN_TERMS.subStep} store={singlePropertyQuoteStore} />,
        onBack: () => handleBack(),
        onNext: () => acceptQuote(),
        buttonText: 'ACCEPT QUOTE',
        allowBack: true,
        allowNext: true,
        type: LoanSubmissionStep.QUOTE_SUMMARY,
        labelIndexNumber: QUOTE_SUMMARY_STEP.QUOTE_SUMMARY_STEP.labelIndexNumber,
        subLabel: QUOTE_SUMMARY_STEP.QUOTE_SUMMARY_STEP.subLabel,
      },
      {
        label: QUOTE_COMPLETE_STEP.QUOTE_COMPLETE_STEP.label,
        icon: null,
        content: <QuoteCompleteStep substep={QUOTE_COMPLETE_STEP.QUOTE_COMPLETE_STEP.subStep} store={singlePropertyQuoteStore} />,
        onBack: () => handleBack(),
        onNext: () => handleComplete(),
        backToHome: true,
        contentHasBorder: false,
        allowBack: false,
        buttonText: 'START ANOTHER LOAN',
        type: LoanSubmissionStep.QUOTE_COMPLETED,
        allowNext: true,
        labelIndexNumber: QUOTE_COMPLETE_STEP.QUOTE_COMPLETE_STEP.labelIndexNumber,
        subLabel: QUOTE_COMPLETE_STEP.QUOTE_COMPLETE_STEP.subLabel,
      }
    ];

    return (
      <Layout>
        <LoanTypeStoreContext.Provider value={singlePropertyQuoteStore}>
          <ProgressBarStepper
            title="Single Property Rental"
            activeStep={singlePropertyQuoteStore.activeStep}
            steps={steps as any}
            onNext={handleNext}
            onBack={handleBack}
          />
        </LoanTypeStoreContext.Provider>
      </Layout>
    );
  }
);
